import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Network,
  Boxes,
  Warehouse,
  Truck,
  // PackageCheck,
  // ClipboardList,
  Route,
  ShieldCheck,
  Activity,
  Headset,
  ArrowRight,
  MoveRight,
  CheckCircle,
  CheckCircle2,
  Workflow,
  Database,
  BarChart3
} from 'lucide-react'

import supplyChainImage from '../assets/images/data/supply-chain.png'
import CTA from './CTA'
import IndustryExpertise from './IndustryExpertise'
import MoreServicesSection from './MoreServicesSection'

const services = [
  {
    number: '01',
    icon: Network,
    title: 'End-to-End Supply Chain Coordination',
    description:
      'Coordinate procurement, transportation, warehousing and distribution through an integrated logistics approach.'
  },
  {
    number: '02',
    icon: Warehouse,
    title: 'Warehousing & Storage',
    description:
      'Organized warehousing and storage support to help businesses manage inventory and coordinate goods movement.'
  },
  {
    number: '03',
    icon: Boxes,
    title: 'Inventory Management Support',
    description:
      'Improve inventory visibility, stock movement coordination and distribution planning.'
  },
  {
    number: '04',
    icon: Truck,
    title: 'Transportation Management',
    description:
      'Coordinate shipment movement through suitable transportation arrangements based on cargo requirements.'
  },
  {
    number: '05',
    icon: Route,
    title: 'Distribution & Fulfilment',
    description:
      'Support distribution through planned movement of goods between warehouses, business locations and customers.'
  },
  {
    number: '06',
    icon: Activity,
    title: 'Shipment Visibility & Tracking',
    description:
      'Get better shipment visibility through tracking information, delivery updates and logistics coordination.'
  }
]

const capabilities = [
  {
    icon: Workflow,
    title: 'Integrated Logistics Planning',
    description:
      'Connect transportation, warehousing and distribution through structured logistics planning.'
  },
  {
    icon: Database,
    title: 'Inventory Visibility',
    description:
      'Support better understanding of stock movement and inventory distribution.'
  },
  {
    icon: Route,
    title: 'Transportation Optimization',
    description:
      'Plan suitable transportation routes and shipment movement for operational efficiency.'
  },
  {
    icon: ShieldCheck,
    title: 'Secure Cargo Handling',
    description:
      'Focus on shipment safety through careful cargo handling and organized logistics processes.'
  },
  {
    icon: BarChart3,
    title: 'Operational Visibility',
    description:
      'Use shipment updates and logistics information to support informed business decisions.'
  },
  {
    icon: Headset,
    title: 'Dedicated Business Support',
    description:
      'Get assistance with shipment coordination and logistics requirements.'
  }
]

const process = [
  {
    number: '01',
    title: 'Understand Requirements',
    description:
      'Understand business requirements, shipment volumes, storage needs and distribution destinations.'
  },
  {
    number: '02',
    title: 'Plan & Coordinate',
    description:
      'Coordinate suitable transportation, warehousing and distribution arrangements.'
  },
  {
    number: '03',
    title: 'Move & Manage',
    description:
      'Manage the movement of goods through planned logistics and distribution operations.'
  },
  {
    number: '04',
    title: 'Deliver & Monitor',
    description:
      'Coordinate final delivery and provide shipment movement visibility.'
  }
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
}

const sectionAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15 },
  variants: fadeUp,
  transition: { duration: 0.6 }
}

export default function SupplyChainManagementPage () {
  return (
    <>
      <Helmet>
        <title>Supply Chain Management | S Infinite Express</title>

        <meta
          name='description'
          content='Simplify your business logistics with S Infinite Express Supply Chain Management solutions. Explore transportation coordination, warehousing, inventory movement, distribution and shipment visibility across India.'
        />

        <meta
          name='keywords'
          content='Supply Chain Management, Supply Chain Logistics India, Logistics Management, Transportation Management, Inventory Management, Warehousing Solutions, Distribution Management, Supply Chain Coordination, Business Logistics, Freight Management, Shipment Tracking, S Infinite Express'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/supply-chain-management'
        />

        <meta
          property='og:title'
          content='Supply Chain Management | S Infinite Express'
        />

        <meta
          property='og:description'
          content='Connect transportation, warehousing and distribution with integrated supply chain logistics solutions from S Infinite Express.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/supply-chain-management'
        />

        <meta property='og:type' content='website' />

        <meta
          property='og:image'
          content='https://sinfiniteexpress.com/static/media/supply-chain.png'
        />

        <meta property='og:site_name' content='S Infinite Express' />
      </Helmet>

      <main className='overflow-hidden bg-white text-gray-800 font-sans'>
        {/* HERO SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-white'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              <motion.div {...sectionAnimation} className='space-y-6'>
                <h1 className='text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-[#211f57] leading-tight'>
                  Smarter Supply Chain
                  <span className='text-[#d32c30]'> Management</span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  helps businesses simplify logistics operations through
                  coordinated transportation, warehousing, distribution and
                  shipment management solutions. We connect different stages of
                  the supply chain to support smooth movement of goods.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  From inventory movement and warehouse coordination to
                  transportation planning and final delivery, our integrated
                  logistics approach helps organizations manage their supply
                  chain requirements effectively.
                </p>

                <div className='flex flex-col sm:flex-row gap-4 pt-3'>
                  <a
                    href='/get-a-quote'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Get a Quote
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='/contact-us'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Contact Us
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                <div className='flex flex-wrap gap-x-6 gap-y-3 pt-5 text-sm font-medium text-gray-600'>
                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Integrated Operations
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Distribution Support
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Shipment Visibility
                  </span>
                </div>
              </motion.div>

              {/* HERO IMAGE */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />

                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={supplyChainImage}
                    alt='S Infinite Express integrated supply chain management and logistics operations'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Bringing Every Part of Your
                  <span className='text-[#d32c30]'> Supply Chain Together</span>
                </h2>

                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we believe an efficient supply chain requires multiple
                  operations to work together. Transportation, warehousing,
                  inventory movement and distribution all play an important role
                  in delivering products to the right destinations.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  Our logistics coordination approach helps businesses connect
                  these activities through organized transportation and
                  distribution processes, supporting different industries and
                  supply chain requirements.
                </p>

                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'Transportation Planning',
                    'Warehouse Coordination',
                    'Inventory Movement',
                    'Distribution Management'
                  ].map((item, index) => (
                    <div key={index} className='flex items-center gap-3'>
                      <CheckCircle2
                        size={20}
                        className='shrink-0 text-green-600'
                      />
                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* CAPABILITIES CARD */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />

                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />

                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    <div className='mb-3 flex items-center gap-4'>
                      <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                        Our Capabilities
                      </span>
                    </div>

                    <p className='mb-4 text-sm leading-7 text-white/70 sm:text-base'>
                      Integrated supply chain solutions designed to simplify
                      logistics operations, improve coordination and support
                      growing business requirements.
                    </p>

                    <div className='space-y-2'>
                      {[
                        {
                          title: 'Integrated Transportation',
                          desc: 'Coordinated transportation planning and cargo movement.'
                        },
                        {
                          title: 'Warehousing & Storage',
                          desc: 'Organized inventory storage and warehouse coordination.'
                        },
                        {
                          title: 'Inventory Management',
                          desc: 'Better stock movement and inventory visibility.'
                        },
                        {
                          title: 'Distribution Management',
                          desc: 'Connected distribution and delivery operations.'
                        }
                      ].map((item, index) => (
                        <div
                          key={index}
                          className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                        >
                          <div>
                            <h4 className='text-sm font-bold text-white sm:text-base'>
                              {item.title}
                            </h4>

                            <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                              {item.desc}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SERVICES */}

        <section className='py-10 bg-[#f7f8fc]'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-7'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                What We Offer
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57] mt-2'>
                Our Supply Chain Management Services
              </h2>

              <p className='text-gray-500 leading-7 mt-2'>
                Integrated logistics solutions to connect transportation,
                warehousing, inventory and distribution operations.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {services.map((service, index) => {
                const Icon = service.icon

                return (
                  <motion.div
                    key={service.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {service.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-4'>
                      {service.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRY EXPERTISE */}

        <IndustryExpertise />

        {/* SUPPLY CHAIN ECOSYSTEM */}
        {/* 
        <section className='py-8 lg:py-10 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-3xl mx-auto mb-7'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                Connected Ecosystem
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57] mt-2'>
                A Connected Journey Across Your Supply Chain
              </h2>

              <p className='text-gray-500 mt-2 leading-7'>
                Connecting every stage of logistics through organized
                transportation, storage and distribution.
              </p>
            </div>

            <div className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#211f57] to-[#2f2c72] p-7 sm:p-10 shadow-xl'>
              <div className='absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-3xl' />

              <div className='relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-7 items-center'>
                {[
                  {
                    icon: ClipboardList,
                    title: 'Procurement',
                    subtitle: 'Material Coordination'
                  },
                  {
                    icon: Warehouse,
                    title: 'Warehousing',
                    subtitle: 'Storage & Inventory'
                  },
                  {
                    icon: Truck,
                    title: 'Transportation',
                    subtitle: 'Cargo Movement'
                  },
                  {
                    icon: Boxes,
                    title: 'Distribution',
                    subtitle: 'Network Management'
                  },
                  {
                    icon: PackageCheck,
                    title: 'Delivery',
                    subtitle: 'Final Destination'
                  }
                ].map((item, index) => {
                  const Icon = item.icon

                  return (
                    <React.Fragment key={item.title}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className='text-center'
                      >
                        <div className='w-16 h-16 mx-auto bg-[#fff44f] rounded-2xl flex items-center justify-center shadow-xl hover:scale-110 transition-transform duration-300'>
                          <Icon className='w-8 h-8 text-[#211f57]' />
                        </div>

                        <h3 className='text-white font-bold mt-4'>
                          {item.title}
                        </h3>

                        <p className='text-blue-200 text-xs mt-2'>
                          {item.subtitle}
                        </p>
                      </motion.div>
                    </React.Fragment>
                  )
                })}
              </div>

              <div className='relative z-10 mt-8 pt-6 border-t border-white/20 text-center'>
                <p className='text-[#fff44f] text-sm font-bold tracking-wide'>
                  ONE CONNECTED LOGISTICS ECOSYSTEM
                </p>

                <p className='text-blue-100 text-sm mt-2'>
                  Coordinating the movement of goods across every stage.
                </p>
              </div>
            </div>
          </div>
        </section> */}

        {/* WHY CHOOSE US */}

        <section className='py-8 lg:py-10 bg-gradient-to-br from-[#211f57] to-[#2f2c72]'>
          <div className='max-w-7xl mx-auto px-5'>
            <div className='text-center max-w-4xl mx-auto mb-5'>
              <span className='text-[#fff44f] font-extrabold text-xs uppercase tracking-[2px]'>
                Why S Infinite Express
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-white'>
                Why Choose Our Supply Chain Solutions?
              </h2>

              <p className='text-blue-100 mt-2 leading-7'>
                Integrated logistics coordination to help businesses manage
                transportation, storage and distribution effectively.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-5'>
              {capabilities.map((feature, index) => {
                const Icon = feature.icon

                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className='group bg-white/10 backdrop-blur-md border border-white/10 p-6 rounded-2xl hover:bg-gradient-to-br hover:from-[#fff44f] hover:to-[#fffcb0] hover:-translate-y-1 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='w-12 h-12 rounded-xl bg-[#fff44f] flex items-center justify-center mb-4 group-hover:bg-white transition-colors'>
                      <Icon className='w-6 h-6 text-[#211f57]' />
                    </div>

                    <h3 className='text-lg font-bold text-white mb-2 group-hover:text-[#211f57] transition-colors'>
                      {feature.title}
                    </h3>

                    <p className='text-sm leading-relaxed text-blue-100 group-hover:text-gray-700 transition-colors'>
                      {feature.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* PROCESS */}

        <section className='py-5 lg:py-8 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-2xl mx-auto mb-7'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                How It Works
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57] mt-2'>
                Our Supply Chain Management Process
              </h2>

              <p className='text-gray-500 mt-2 leading-7'>
                A simple and coordinated approach to managing logistics
                activities from beginning to end.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {process.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className='relative text-center'
                >
                  <div className='w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-[#fff44f] flex items-center justify-center text-xl font-extrabold shadow-lg'>
                    {step.number}
                  </div>

                  <h3 className='text-lg font-extrabold text-[#211f57] mt-5'>
                    {step.title}
                  </h3>

                  <p className='text-sm text-gray-500 leading-7 mt-2'>
                    {step.description}
                  </p>

                  {index !== process.length - 1 && (
                    <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}

        <section className='relative bg-[#211f57] py-8 sm:py-10 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />

          <div className='absolute left-0 bottom-0 w-72 h-72 rounded-full bg-[#d32c30]/10 blur-3xl' />

          <motion.div
            {...sectionAnimation}
            className='relative max-w-5xl mx-auto px-6 text-center'
          >
            <h2 className='text-xl sm:text-2xl lg:text-3xl font-extrabold text-white leading-tight'>
              Simplify Your Supply Chain.{' '}
              <span className='text-[#fff44f]'>Strengthen Your Business.</span>
            </h2>
            <p className='text-white/70 max-w-4xl mx-auto mt-4 leading-8'>
              S Infinite Express delivers integrated transportation,
              warehousing, and distribution for efficient logistics.
            </p>

            <a
              href='/get-a-quote'
              className='inline-flex items-center justify-center gap-3 mt-6 px-7 py-3.5 bg-[#fff44f] text-[#211f57] font-extrabold rounded-xl shadow-lg hover:bg-white hover:-translate-y-1 transition-all duration-300'
            >
              Get a Supply Chain Quote
              <MoveRight className='w-5 h-5' />
            </a>
          </motion.div>
        </section>

        {/* MORE SERVICES */}
        <MoreServicesSection />

        {/* COMMON CTA */}
        <CTA />
      </main>
    </>
  )
}
